import { Button, ButtonLevel, ButtonSize, Text, TextColor, TextSize } from '@vizit/atoms';
import { WarningTriangleIcon } from '@vizit/icons';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import { AVC_DISCONNECT_DIALOG_COPY } from './avc-disconnect-dialog.constants';

interface AvcDisconnectDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * Account the dialog is about, shown so the user knows which connection they are dropping.
   *
   * @optional
   * @type {string}
   */
  accountLabel?: string;
  /**
   * Callback fired when the user confirms the disconnect.
   *
   * @optional
   * @type {() => void}
   */
  onConfirm?: () => void;
  /**
   * Whether the disconnect request is in flight.
   *
   * @optional
   * @default false
   * @type {boolean}
   */
  isDisconnecting?: boolean;
}

/**
 * Confirmation dialog for disconnecting an Amazon Vendor Central account. It spells out that
 * disconnecting only clears the token Vizit holds — the authorization itself has to be removed by
 * the customer in Vendor Central, because Amazon exposes no revoke we can call.
 */
function AvcDisconnectDialog(props: AvcDisconnectDialogProps) {
  const { accountLabel, onOpenChange, onConfirm, isDisconnecting = false, isOpen = false, ...other } = props;

  const handleClose = () => onOpenChange?.(false);

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content className="w-[440px] max-[320px]:w-full" dialogClassName="gap-4 max-w-[440px]">
          <Dialog.Header
            variant={DialogHeaderVariant.Icon}
            status={DialogHeaderStatus.Warning}
            icon={WarningTriangleIcon}
          >
            <Dialog.Title showEllipsis={false}>{AVC_DISCONNECT_DIALOG_COPY.title}</Dialog.Title>
          </Dialog.Header>
          <div className="flex flex-col gap-3 px-12">
            <Dialog.Subtitle elementType="p" size={TextSize.Md} className="text-secondary" showEllipsis={false}>
              {accountLabel
                ? `${AVC_DISCONNECT_DIALOG_COPY.description} Account: ${accountLabel}.`
                : AVC_DISCONNECT_DIALOG_COPY.description}
            </Dialog.Subtitle>
            <Text
              elementType="p"
              size={TextSize.Sm}
              color={TextColor.Tertiary}
              showEllipsis={false}
              data-testid="avc-disconnect-amazon-note"
            >
              {AVC_DISCONNECT_DIALOG_COPY.amazonNote}
            </Text>
          </div>
          <Dialog.Footer className="grid grid-cols-2 gap-6">
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Secondary}
              onPress={handleClose}
              isDisabled={isDisconnecting}
            >
              {AVC_DISCONNECT_DIALOG_COPY.cancel}
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Danger}
              onPress={onConfirm}
              isLoading={isDisconnecting}
            >
              {AVC_DISCONNECT_DIALOG_COPY.confirm}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { AvcDisconnectDialog };
export type { AvcDisconnectDialogProps };
